import { social } from '../api/social.js';
import { showToast } from './Toast.js';
import { messageFor } from '../api/errors.js';
import { realtime } from '../api/realtime.js';
import { shieldFromPhaser } from './uiShield.js';
import { giftButton } from '../farm/fuelGifts.mjs';
import { audioEvents } from '../audio/audioEvents.js';
import { iconHtml } from './iconHtml.mjs';
import { lockInput, unlockInput } from './inputLock.js';
import { lastSeenText, giftRowText, sortFriends } from './friendsPanelView.mjs';
import { gameState } from '../state/gameState.js';
import { giftsView, countBadge } from '../farm/fuelGifts.mjs';
import { friendPortrait } from './friendPortrait.js';
import { serverNow } from '../state/serverClock.mjs';
import { trackPanel } from './panelSwitch.js';

// The friends panel in the game's wooden look (owner 2026-10-01: wider, not see-through, a
// portrait per friend with name, level and last active, clear buttons). Plain DOM over the
// canvas. Usage: openFriendsPanel(scene, { onVisit: (hostId, hostName) => {...} })
const CSS = `
#friends-panel { position:fixed; top:0; right:0; bottom:0; width:min(460px, 100vw); z-index:1000;
  display:flex; flex-direction:column; background:#fff3dc; color:#5a3414; box-sizing:border-box;
  border-left:6px solid #8d5a2b; box-shadow:-8px 0 24px #0007; font-size:16px; }
#friends-panel * { box-sizing:border-box; }
#friends-panel .fp-head { display:flex; align-items:center; gap:12px; padding:12px 14px 12px 18px;
  background:linear-gradient(#d9a066, #b57a43); border-bottom:4px solid #7a4b24; }
#friends-panel .fp-head h2 { margin:0; flex:1; font-size:28px; color:#fff8e8;
  text-shadow:0 2px 0 #5a3414, 2px 0 0 #5a3414, -2px 0 0 #5a3414, 0 -2px 0 #5a3414; }
#friends-panel .fp-body { flex:1; overflow-y:auto; padding:14px 16px 20px; }
#friends-panel h3 { margin:16px 2px 8px; font-size:15px; letter-spacing:.06em; text-transform:uppercase; color:#8d5a2b; }
#friends-panel .fp-add { display:flex; gap:8px; align-items:flex-start; }
#friends-panel input { flex:1; min-width:0; height:48px; padding:0 14px; border:3px solid #c59a3e; border-radius:14px;
  background:#fff; color:#3a2a1a; font-size:17px; outline:none; }
#friends-panel input:focus { border-color:#8d5a2b; }
/* Grandstander sits high in its line box: a few px of top padding centres the label on the face.
   The face + its 5 px 3D lip is 48 px — the same as the name input beside "Dodaj". */
#friends-panel .fp-btn { height:43px; padding:3px 16px 0; border:none; border-radius:14px; cursor:pointer;
  font-size:16px; font-weight:700; line-height:1; white-space:nowrap; transition:transform .06s, box-shadow .06s;
  display:inline-flex; align-items:center; justify-content:center; gap:6px; }
#friends-panel .fp-btn img { display:block; }
#friends-panel .fp-btn:active:not(:disabled) { transform:translateY(4px); }
#friends-panel .fp-primary { background:#6cbf45; color:#fff; box-shadow:0 5px 0 #3f8a25, inset 0 2px 0 #9be07a; text-shadow:0 1px 0 #2d6b19; }
#friends-panel .fp-primary:active:not(:disabled) { box-shadow:0 1px 0 #3f8a25, inset 0 2px 0 #9be07a; }
#friends-panel .fp-secondary { background:#f2d38a; color:#5a3a12; box-shadow:0 5px 0 #c59a3e, inset 0 2px 0 #fbe9bd; }
#friends-panel .fp-secondary:active:not(:disabled) { box-shadow:0 1px 0 #c59a3e, inset 0 2px 0 #fbe9bd; }
#friends-panel .fp-danger { background:#e0604d; color:#fff; box-shadow:0 5px 0 #a8382a, inset 0 2px 0 #f29a8c; }
#friends-panel .fp-btn:disabled { background:#e4ded2; color:#8a8378; box-shadow:0 5px 0 #b9b1a3; cursor:default; }
#friends-panel .fp-close { width:46px; height:46px; padding:0; border-radius:50%; background:#7a4b24;
  color:#fff8e8; box-shadow:0 4px 0 #4a2c12; font-size:20px; }
#friends-panel .fp-card { position:relative; display:grid; grid-template-columns:72px 1fr; gap:6px 12px; align-items:center;
  background:#fffaf0; border:2px solid #e8cfa0; border-radius:18px; padding:10px; margin:0 0 10px; }
#friends-panel .fp-face { grid-row:span 2; position:relative; width:72px; height:72px; border-radius:16px;
  background:linear-gradient(#cfe9f7, #f6e3bd); border:3px solid #d9a066; overflow:hidden; }
#friends-panel .fp-face img { width:100%; height:100%; object-fit:cover; }
#friends-panel .fp-dot { position:absolute; right:3px; bottom:3px; width:16px; height:16px; border-radius:50%;
  border:2px solid #fff; background:#b9b1a3; }
#friends-panel .fp-dot.on { background:#4cc35a; }
#friends-panel .fp-info { min-width:0; padding-right:30px; }   /* clear of the remove button */
#friends-panel .fp-name { font-size:19px; font-weight:800; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
#friends-panel .fp-meta { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-top:3px; font-size:14px; color:#7a5a3a; }
#friends-panel .fp-level { background:#ffe7a3; border:2px solid #e3b44a; border-radius:999px; padding:1px 9px; font-weight:800; color:#7a4b24; }
#friends-panel .fp-acts { display:flex; gap:8px; flex-wrap:wrap; }
#friends-panel .fp-acts .fp-btn { flex:1 1 auto; height:34px; font-size:15px; border-radius:12px; padding-top:2px; }
/* the friend-row buttons are slimmer (owner 2026-10-01): a 4 px lip */
#friends-panel .fp-acts .fp-primary { box-shadow:0 4px 0 #3f8a25, inset 0 2px 0 #9be07a; }
#friends-panel .fp-acts .fp-secondary { box-shadow:0 4px 0 #c59a3e, inset 0 2px 0 #fbe9bd; }
#friends-panel .fp-acts .fp-btn:disabled { box-shadow:0 4px 0 #b9b1a3; }
/* Remove a friend (owner 2026-10-20): a small x in the corner, then a question in the card. */
#friends-panel .fp-remove { position:absolute; top:6px; right:6px; width:28px; height:28px; padding:0; border:none;
  border-radius:50%; background:transparent; cursor:pointer; opacity:.45; display:flex; align-items:center; justify-content:center; }
#friends-panel .fp-remove:hover { opacity:.9; background:#f3e2c2; }
#friends-panel .fp-remove img { width:16px; height:16px; }
#friends-panel .fp-ask { display:none; align-items:center; gap:8px; flex-wrap:wrap; }
#friends-panel .fp-ask span { flex:1 1 100%; font-weight:700; }
#friends-panel .fp-ask .fp-btn { flex:1 1 auto; height:34px; font-size:15px; border-radius:12px; padding-top:2px; }
#friends-panel .fp-card.asking .fp-acts { display:none; }
#friends-panel .fp-card.asking .fp-ask { display:flex; }
#friends-panel .fp-empty { margin:4px 2px 0; color:#8d6b4a; font-style:italic; }
#friends-panel .fp-req { display:flex; align-items:center; gap:8px; background:#fffaf0; border:2px dashed #e3b44a;
  border-radius:16px; padding:8px 8px 8px 14px; margin-bottom:8px; }
#friends-panel .fp-gift { display:flex; align-items:center; gap:10px; background:#fff8dc; border:2px solid #e3b44a;
  border-radius:16px; padding:8px 8px 8px 12px; margin-bottom:8px; }
#friends-panel .fp-gift .fp-can { width:34px; height:34px; flex:0 0 auto; display:flex; align-items:center; justify-content:center; font-size:26px; }
#friends-panel .fp-gift b { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
#friends-panel .fp-gift .fp-count { flex:0 0 auto; font-weight:bold; color:#b8860b; }
#friends-panel .fp-gift .fp-btn { height:34px; font-size:15px; border-radius:12px; padding-top:2px; }
#friends-panel .fp-req b { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
`;

function ensureStyle() {
  if (document.getElementById('fp-style')) return;
  const st = document.createElement('style');
  st.id = 'fp-style';
  st.textContent = CSS;
  document.head.appendChild(st);
}

export function openFriendsPanel(scene, { onVisit } = {}) {
  if (document.getElementById('friends-panel')) return;
  ensureStyle();

  const root = document.createElement('div');
  root.id = 'friends-panel';
  root.innerHTML = `
    <div class="fp-head">
      <h2>${iconHtml('Znajomi {friends}')}</h2>
      <button id="fp-close" class="fp-btn fp-close" aria-label="Zamknij">${iconHtml('{cross}')}</button>
    </div>
    <div class="fp-body">
      <div class="fp-add">
        <input id="fp-name" placeholder="Imię kolegi lub koleżanki" autocomplete="off"/>
        <button id="fp-add" class="fp-btn fp-primary">${iconHtml('{plus} Dodaj')}</button>
      </div>
      <div id="fp-gifts-wrap"><h3>${iconHtml('Prezenty od znajomych {canister}')}</h3><div id="fp-gifts"></div></div>
      <div id="fp-requests-wrap"><h3>Zaproszenia</h3><div id="fp-requests"></div></div>
      <h3>Twoi znajomi</h3><div id="fp-list"></div>
    </div>
  `;
  document.body.appendChild(root);
  // Keep panel clicks (e.g. accepting a request) and typing from leaking to Phaser's
  // window-level handlers — otherwise the click passes through to canvas buttons
  // underneath and WASD typing walks the player.
  shieldFromPhaser(root);

  let offPresence = null;
  let closed = false;
  lockInput(scene);   // Natalka stays put and the phone's joystick / action button step aside
  let untrack = () => {};
  const close = () => {
    if (closed) return;
    closed = true;
    untrack();
    unlockInput(scene);
    offPresence?.();
    scene.events.off('shutdown', close);
    root.remove();
  };
  root.querySelector('#fp-close').onclick = close;
  untrack = trackPanel(scene, 'friends', close, { dom: true });   // a tap on the game beside it closes it (panelSwitch.js)
  scene.events.once('shutdown', close);

  const nameInput = root.querySelector('#fp-name');
  const add = async () => {
    const name = nameInput.value.trim();
    if (!name) { nameInput.focus(); return; }
    try {
      await social.sendRequest(name);
      showToast(scene, 'Wysłano zaproszenie {letter}');
      nameInput.value = '';
    } catch (e) {
      showToast(scene, messageFor(e));
    }
  };
  root.querySelector('#fp-add').onclick = add;
  nameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') add(); });

  // Friends' canisters not seen yet (the red dot on Znajomi): listed here with a thank-you, and
  // marked seen — on the farm through its batch (FarmScene._seenGifts), elsewhere straight away.
  const giftsAtOpen = [...(gameState.raw?.farm?.new_gifts ?? [])];   // kept while the panel is open
  let giftsMarked = false;
  // A friend can be sent a canister from two places here — their gift row up top and their card below — so
  // every button for one friend is painted together (owner 2026-10-27: after a thank-you the card still
  // offered "Wyślij paliwo"). Filled by render(), which rebuilds both.
  let giftPainters = new Map();   // friend id -> [paint(sent)]
  const onGiftPaint = (id, paint) => { if (!giftPainters.has(id)) giftPainters.set(id, []); giftPainters.get(id).push(paint); };
  const paintSent = (id, sent) => { for (const p of giftPainters.get(id) ?? []) p(sent); };
  async function sendGift(f) {
    paintSent(f.id, true);                                  // at once: a double tap sends nothing twice
    try {
      await social.sendFuelGift(f.id);
      audioEvents.emit('buy');
      showToast(scene, `Wysłano kanister {fuel} — ${f.name}`);
    } catch (e) {
      if (e?.error_code !== 'GIFT_ALREADY_SENT') { paintSent(f.id, false); showToast(scene, messageFor(e)); return; }
    }
    const shownF = shown.find((x) => x.id === f.id);
    if (shownF) shownF.gift_sent_today = true;
  }
  function renderGifts(friends) {
    const gifts = giftsAtOpen;
    const wrap = root.querySelector('#fp-gifts-wrap'), box = root.querySelector('#fp-gifts');
    wrap.style.display = gifts.length ? '' : 'none';
    box.innerHTML = '';
    if (!gifts.length) return;
    // One row per friend, "×3" when they sent three (owner 2026-10-27).
    const view = giftsView(gifts, new Set(friends.filter((f) => f.gift_sent_today).map((f) => f.id)));
    view.rows.forEach((r) => {
      const row = document.createElement('div');
      row.className = 'fp-gift';
      row.innerHTML = `<span class="fp-can">${iconHtml('{canister}')}</span><b></b><span class="fp-count"></span><button class="fp-btn fp-primary"></button>`;
      row.querySelector('b').textContent = giftRowText(r);
      row.querySelector('.fp-count').textContent = countBadge(r.count);
      const btn = row.querySelector('button');
      const paint = (sent) => {
        const t = sent ? { label: '{check} Wysłano', enabled: false } : { label: 'Podziękuj {fuel}', enabled: true };
        btn.innerHTML = iconHtml(t.label); btn.disabled = !t.enabled;
      };
      paint(!r.thank.enabled);
      onGiftPaint(r.id, paint);
      btn.onclick = () => sendGift(r);
      box.appendChild(row);
    });
    if (!giftsMarked) { giftsMarked = true; markGiftsSeen(gifts.length); }
  }
  function markGiftsSeen(count) {
    if (typeof scene._seenGifts === 'function') { scene._seenGifts(count); return; }
    const op = { op: 'seen_gifts', cells: [], seed: null, tool: null, count };
    if (gameState.applyFarmLocal(op).ok) { gameState.emit(); gameState.farmActions([op]).catch(() => {}); }
  }

  async function render() {
    let [reqs, friends] = await Promise.all([social.listRequests(), social.listFriends()]);
    giftPainters = new Map();
    renderGifts(friends);
    const reqWrap = root.querySelector('#fp-requests-wrap');
    const reqBox = root.querySelector('#fp-requests');
    reqWrap.style.display = reqs.length ? '' : 'none';            // no empty "brak" section
    reqBox.innerHTML = '';
    // names via textContent — usernames are app-controlled but avoid HTML injection
    reqs.forEach((rq) => {
      const row = document.createElement('div');
      row.className = 'fp-req';
      row.innerHTML = `<b></b>
        <button class="fp-btn fp-primary" data-acc="1">${iconHtml('{check} Przyjmij')}</button>
        <button class="fp-btn fp-danger" data-acc="0" aria-label="Odrzuć">${iconHtml('{cross}')}</button>`;
      row.querySelector('b').textContent = rq.from_name;
      row.querySelector('[data-acc="1"]').onclick = async () => { await social.respond(rq.id, true); render(); };
      row.querySelector('[data-acc="0"]').onclick = async () => { await social.respond(rq.id, false); render(); };
      reqBox.appendChild(row);
    });

    const listBox = root.querySelector('#fp-list');
    friends = sortFriends(friends);
    shown = friends;
    listBox.innerHTML = friends.length ? ''
      : '<p class="fp-empty">Nie masz jeszcze znajomych — wpisz imię kolegi lub koleżanki i kliknij „Dodaj”.</p>';
    friends.forEach((f) => {
      const card = document.createElement('div');
      card.className = 'fp-card';
      card.dataset.friendId = f.id;
      card.innerHTML = `
        <div class="fp-face"><img alt=""/><span class="fp-dot${f.online ? ' on' : ''}"></span></div>
        <div class="fp-info">
          <div class="fp-name"></div>
          <div class="fp-meta"><span class="fp-level">${iconHtml(`{star_full} Poziom ${Number(f.level) || 1}`)}</span><span class="fp-when"></span></div>
        </div>
        <div class="fp-acts">
          <button class="fp-btn fp-primary" data-visit>${iconHtml('{house} Odwiedź')}</button>
          <button class="fp-btn fp-secondary" data-gift></button>
        </div>
        <div class="fp-ask">
          <span>Usunąć ze znajomych?</span>
          <button class="fp-btn fp-danger" data-remove-yes>Usuń</button>
          <button class="fp-btn fp-secondary" data-remove-no>Anuluj</button>
        </div>
        <button class="fp-remove" data-remove aria-label="Usuń ze znajomych">${iconHtml('{cross}')}</button>`;
      card.querySelector('.fp-name').textContent = f.name;
      card.querySelector('.fp-when').textContent = lastSeenText(f.last_seen_at, serverNow(), f.online);
      const img = card.querySelector('.fp-face img');
      img.style.visibility = 'hidden';
      friendPortrait(scene, f).then((src) => { if (src) { img.src = src; img.style.visibility = ''; } });
      const giftBtn = card.querySelector('[data-gift]');
      const paint = (sent) => { const b = giftButton({ gift_sent_today: sent }); giftBtn.innerHTML = iconHtml(b.label); giftBtn.disabled = !b.enabled; };   // our own label, escaped by iconHtml
      paint(!!f.gift_sent_today);
      onGiftPaint(f.id, paint);
      giftBtn.onclick = () => sendGift(f);
      card.querySelector('[data-visit]').onclick = () => { close(); onVisit?.(f.id, f.name); };
      card.querySelector('[data-remove]').onclick = () => card.classList.toggle('asking');
      card.querySelector('[data-remove-no]').onclick = () => card.classList.remove('asking');
      const yes = card.querySelector('[data-remove-yes]');
      yes.onclick = async () => {
        yes.disabled = true;                                   // one tap, one removal
        try {
          await social.removeFriend(f.id);
          showToast(scene, `Usunięto ze znajomych: ${f.name}`);
          render().catch((e) => showToast(scene, messageFor(e)));
        } catch (e) { yes.disabled = false; showToast(scene, messageFor(e)); }
      };
      listBox.appendChild(card);
    });
  }

  let shown = [];   // the friends as listed, kept in order as they come online / go offline
  render().catch((e) => showToast(scene, messageFor(e)));
  root._render = render;  // a later task calls this to live-refresh

  offPresence = realtime.on('presence', (m) => {
    const card = root.querySelector(`[data-friend-id="${m.friendId}"]`);
    if (!card) return;
    card.querySelector('.fp-dot')?.classList.toggle('on', !!m.online);
    const whenEl = card.querySelector('.fp-when');
    if (whenEl) whenEl.textContent = lastSeenText(new Date().toISOString(), serverNow(), m.online);
    const f = shown.find((x) => x.id === m.friendId);
    if (f) {
      Object.assign(f, { online: !!m.online, last_seen_at: new Date(serverNow()).toISOString() });
      shown = sortFriends(shown);
      const listBox = root.querySelector('#fp-list');
      shown.forEach((x) => { const c = listBox.querySelector(`[data-friend-id="${x.id}"]`); if (c) listBox.appendChild(c); });
    }
  });

  return root;
}
